import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { XIcon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { useRole } from '@/hooks/use-role';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  employeeQueryKey,
  getEmployee,
  inviteEmployee,
  type EmployeeInviteLinkDto,
  type UpdateEmployeeRequest,
} from '@/lib/api/employees';

import { editEmployeeSchema, type EditEmployeeInput } from './schema';
import { InviteLinkPanel } from './invite-link-panel';

interface Props {
  businessId: string;
  employeeId: string;
  onSave: (body: UpdateEmployeeRequest) => void;
  onClose: () => void;
  isSaving: boolean;
}

const DEFAULT_VALUES: EditEmployeeInput = {
  title: '',
  status: 'active',
  hiredAt: '',
};

export function EmployeeDetailModal({
  businessId,
  employeeId,
  onSave,
  onClose,
  isSaving,
}: Props) {
  const { t } = useLanguage();
  const { isOwner } = useRole();

  const [inviteLink, setInviteLink] = useState<EmployeeInviteLinkDto | null>(null);

  const employeeQuery = useQuery({
    queryKey: employeeQueryKey(businessId, employeeId),
    queryFn: () => getEmployee(businessId, employeeId),
  });

  const inviteMutation = useMutation({
    mutationFn: () => inviteEmployee(businessId, employeeId),
    meta: { successMessage: t.employeeInviteLinkReady },
    onSuccess: (data) => setInviteLink(data),
  });

  const form = useForm<EditEmployeeInput>({
    defaultValues: DEFAULT_VALUES,
    resolver: zodResolver(editEmployeeSchema),
  });

  useEffect(() => {
    if (!employeeQuery.data) return;
    form.reset({
      title: employeeQuery.data.title ?? '',
      status: employeeQuery.data.status,
      hiredAt: employeeQuery.data.hired_at ? employeeQuery.data.hired_at.slice(0, 10) : '',
    });
  }, [employeeQuery.data, form]);

  function handleSubmit(data: EditEmployeeInput) {
    onSave({
      title: data.title || undefined,
      status: data.status,
      hired_at: data.hiredAt || undefined,
    });
  }

  const employee = employeeQuery.data;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-card border rounded-xl shadow-xl w-full max-w-lg flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
          <h2 className="font-semibold text-base">{employee?.full_name ?? t.loading}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors rounded-md p-1"
            data-testid="modal-close"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        {employeeQuery.isPending ? (
          <p className="px-6 py-8 text-sm text-muted-foreground">{t.loading}</p>
        ) : employeeQuery.isError ? (
          <p className="px-6 py-8 text-sm text-destructive">{t.employeeLoadError}</p>
        ) : (
          <form onSubmit={form.handleSubmit(handleSubmit)} className="overflow-y-auto flex-1">
            <div className="px-6 py-5 flex flex-col gap-4">
              {/* Read-only, denormalized from the linked user record */}
              <div className="flex flex-col gap-1.5 text-sm">
                <span className="text-muted-foreground text-xs">{t.employeeEmail}</span>
                <span>{employee?.email ?? '—'}</span>
              </div>
              <div className="flex flex-col gap-1.5 text-sm">
                <span className="text-muted-foreground text-xs">{t.employeePhone}</span>
                <span>{employee?.phone ?? '—'}</span>
              </div>

              {isOwner && (
                <div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={!employee?.email || inviteMutation.isPending}
                    title={!employee?.email ? t.employeeNoEmailTooltip : undefined}
                    onClick={() => inviteMutation.mutate()}
                    data-testid="employee-invite-link"
                  >
                    {t.employeeInviteLink}
                  </Button>
                </div>
              )}

              {inviteLink && (
                <InviteLinkPanel
                  link={inviteLink}
                  recipientEmail={employee?.email ?? null}
                  onClose={() => setInviteLink(null)}
                />
              )}

              <Controller
                control={form.control}
                name="title"
                render={({ field, fieldState }) => (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="edit-employee-title">{t.employeeTitle}</Label>
                    <Input
                      {...field}
                      id="edit-employee-title"
                      placeholder={t.employeeTitlePlaceholder}
                      aria-invalid={fieldState.invalid}
                      data-testid="edit-employee-title-input"
                    />
                    {fieldState.error && (
                      <p className="text-xs text-destructive">{fieldState.error.message}</p>
                    )}
                  </div>
                )}
              />

              <Controller
                control={form.control}
                name="status"
                render={({ field }) => (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="edit-employee-status">{t.employeeStatus}</Label>
                    <select
                      {...field}
                      id="edit-employee-status"
                      data-testid="edit-employee-status-select"
                      className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="active">{t.employeeActive}</option>
                      <option value="inactive">{t.employeeInactive}</option>
                    </select>
                  </div>
                )}
              />

              <Controller
                control={form.control}
                name="hiredAt"
                render={({ field, fieldState }) => (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="edit-employee-hired-at">{t.employeeHiredAt}</Label>
                    <Input
                      {...field}
                      id="edit-employee-hired-at"
                      type="date"
                      aria-invalid={fieldState.invalid}
                      data-testid="edit-employee-hired-at-input"
                    />
                    {fieldState.error && (
                      <p className="text-xs text-destructive">{fieldState.error.message}</p>
                    )}
                  </div>
                )}
              />
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 px-6 py-4 border-t shrink-0">
              <Button type="button" variant="outline" size="sm" onClick={onClose}>
                {t.cancel}
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={isSaving}
                data-testid="save-employee-edit"
              >
                {t.saveEmployee}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
